/**
 * The `/educators/*` virtual route, and what it is for.
 *
 * The educators app is a separate Next.js app on its own origin (`educators.zoolife.tv`), but a
 * handful of things the API and Stripe send people to are hardcoded to zl-app's origin: the
 * checkout return URLs (`services/stripe.ts` builds them from `ZOOLIFE_APP_ROOT`), the OAuth
 * callback, and the email verification link. An educators visitor therefore lands *here* and has to
 * be sent on.
 *
 * `Main`'s `/educators/:educatorPath*` route is that hop, and this module is the address book it
 * and the popup return routes share.
 *
 * ## Why the popups hop rather than posting across origins
 *
 * The Speedy Flow's checkout and SSO tabs report back to the page that opened them with
 * `postMessage`, and both ends of that conversation are same-origin by construction — the sender
 * posts with `window.location.origin` as the target, and the receiver drops anything from another
 * origin. A tab sitting on zl-app cannot talk to an opener on educators.
 *
 * Rather than widening either end to an origin allowlist, the tab *follows* the hop: it arrives on
 * the educators origin, which is where its opener is, and reports from there. `window.opener`
 * survives cross-origin navigation — the SSO round trip already relies on this, and neither app
 * sets `Cross-Origin-Opener-Policy` — so nothing is lost by going the long way round, and no
 * cross-origin messaging is introduced anywhere in the system.
 */

/** The prefix that marks a path as belonging to the educators app rather than to zl-app. */
export const EDUCATORS_PATH_PREFIX = '/educators';

/**
 * Whether a path is destined for the educators app.
 *
 * Matches the prefix exactly or as a whole segment, so `/educatorsomething` is not an educators
 * path. Paths arrive from `co-redirect` and from the post-auth destination cookie, both of which
 * are ultimately attacker-supplied, and the answer decides whether we navigate somewhere.
 */
export const isEducatorsPath = (path: string | null | undefined): path is string =>
  !!path && (path === EDUCATORS_PATH_PREFIX || path.startsWith(`${EDUCATORS_PATH_PREFIX}/`));

/**
 * Turn an `/educators/...` path into an absolute URL on the educators origin, stripping the prefix
 * that only ever existed to get it routed here.
 *
 * Returns undefined when there is no configured educators app, which is the honest answer: the
 * caller has nowhere to send anybody and should do whatever it would have done without one.
 *
 * `extraSearch` is merged *under* the path's own query string, so a parameter the path carries
 * deliberately — `sf`, the popup marker — is never overwritten by one that came along for the ride.
 */
export const toEducatorsURL = (
  educatorsAppURL: string | undefined,
  path: string,
  extraSearch?: URLSearchParams | string,
): string | undefined => {
  if (!educatorsAppURL) {
    return undefined;
  }
  try {
    const [pathname, ownSearch = ''] = path.split('?');
    const url = new URL(educatorsAppURL);
    url.pathname = pathname.slice(EDUCATORS_PATH_PREFIX.length) || '/';

    const params = new URLSearchParams(extraSearch);
    new URLSearchParams(ownSearch).forEach((value, key) => params.set(key, value));
    url.search = params.toString();

    return url.toString();
  } catch {
    // A malformed educators URL in remote config. Nowhere to go.
    return undefined;
  }
};
